"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { useT } from "@/hooks/useT";

export interface HomeBlogPost {
  slug: string;
  title: string;
  excerpt: string;
  cover_image_url: string | null;
  tags: string[];
  published_at: string | null;
}

/**
 * HomeBlogPosts — a grid of recent blog articles, placed before the FAQ to add
 * dynamism and feed internal links (good for SEO). Server-fed (posts prop),
 * design-system clean. Hidden entirely when there are no posts.
 */
export default function HomeBlogPosts({ posts = [] }: { posts?: HomeBlogPost[] }) {
  const t = useT();
  const items = (posts || []).filter((p) => p?.slug && p?.title).slice(0, 6);
  if (items.length === 0) return null;

  return (
    <section className="py-20 lg:py-28 bg-gray-50 dark:bg-[#0f0f12]">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10 lg:mb-14">
          <div className="max-w-2xl">
            <div className="font-inter-tight text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
              {t("home.blog.eyebrow")}
            </div>
            <h2 className="font-inter-tight font-semibold text-gray-900 dark:text-white tracking-[-0.02em] text-[34px] lg:text-[48px] leading-[1.05]">
              {t("home.blog.title")}
            </h2>
          </div>
          <Link
            href="/blog"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[5px] border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 font-inter-tight font-medium text-sm hover:bg-white dark:hover:bg-white/[0.04] transition-colors self-start lg:self-auto"
          >
            {t("home.blog.cta")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {items.map((post) => {
            const tag = Array.isArray(post.tags) ? post.tags[0] : undefined;
            const date = post.published_at
              ? new Date(post.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : null;
            return (
              <Link
                key={post.slug}
                href={`/blog/${post.slug}`}
                prefetch={false}
                className="group flex flex-col rounded-[10px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] overflow-hidden transition-colors hover:border-gray-300 dark:hover:border-white/20"
              >
                {post.cover_image_url && (
                  <div className="relative aspect-[1200/630] overflow-hidden bg-gray-100 dark:bg-[#0a0a0b]">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={post.cover_image_url}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                  </div>
                )}
                <div className="flex flex-col flex-1 p-4">
                  {tag && (
                    <div className="font-inter-tight text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-2">
                      {tag}
                    </div>
                  )}
                  <h3 className="font-inter-tight font-semibold text-[16px] text-gray-900 dark:text-white leading-snug line-clamp-2 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="mt-2 text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                      {post.excerpt}
                    </p>
                  )}
                  <div className="mt-auto pt-4 flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500">
                    {date ? <span>{date}</span> : <span />}
                    <span className="inline-flex items-center gap-1 font-medium text-gray-900 dark:text-white">
                      {t("home.blog.readMore")}
                      <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
